import type { ComponentPropsWithRef, FC } from 'react';

import type { ClassNameProps } from '@tripleten/cn';

import { cnSpinner } from './spinner.constants.ts';

import './spinner.css';

import './__container/spinner__container.css';
import './__content/spinner__content.css';
import './__leaf/spinner__leaf.css';
import './__overlay/spinner__overlay.css';

import './_size/spinner_size.css';

const LEAVES = Array.from({ length: 8 });

export type SpinnerProps = ClassNameProps &
    ComponentPropsWithRef<'span'> & {
        /**
         * Size of the spinner.
         */
        size?: '1' | '2' | '3';
        /**
         * While `true`, renders the spinner; when `false`, renders the children as-is.
         */
        loading?: boolean;
    };

/**
 * A spinning loading indicator.
 *
 * Standalone:
 * ```tsx
 * <Spinner size="3" />
 * ```
 *
 * Wrapping content: while `loading`, the children are kept for layout but hidden and the spinner is
 * centered over them; once `loading` is `false`, the children render normally.
 * ```tsx
 * <Spinner loading={isPending}>
 *   <Button>Save</Button>
 * </Spinner>
 * ```
 */
export const Spinner: FC<SpinnerProps> = ({ size = '2', loading = true, className, children, ...props }) => {
    if (!loading) {
        return children;
    }

    const spinner = (
        <span {...props} className={cnSpinner({ size }, [className])}>
            {LEAVES.map((_, index) => (
                <span key={index} className={cnSpinner('leaf')} />
            ))}
        </span>
    );

    if (children === undefined) {
        return spinner;
    }

    return (
        <span className={cnSpinner('container')}>
            {/*
             * `display: contents` removes the content from the accessibility tree in some browsers,
             * so it is also forced out with `aria-hidden`.
             */}
            <span aria-hidden className={cnSpinner('content')}>
                {children}
            </span>

            <span className={cnSpinner('overlay')}>{spinner}</span>
        </span>
    );
};
